import React, { useMemo, useState } from 'react';
import { ConversationTurn, ColumnFocus } from '../types/chat';
import { useTheme } from '../utils/theme';
import MessageBubble from './MessageBubble';
import { getT } from '../utils/i18n';

interface TurnRowProps {
  turn: ConversationTurn;
  sourceLanguage: string;
  processingLanguage: string;
  columnFocus: ColumnFocus;
}

// Pre-computed grid styles to avoid creating new objects on every render
const GRID_STYLES: Record<ColumnFocus, React.CSSProperties> = {
  left:  { gridTemplateColumns: '100% 0%',  transition: 'grid-template-columns 260ms ease-in-out' },
  right: { gridTemplateColumns: '0% 100%',  transition: 'grid-template-columns 260ms ease-in-out' },
  both:  { gridTemplateColumns: '50% 50%',  transition: 'grid-template-columns 260ms ease-in-out' },
};

// Collapsible thinking block — shows model's chain-of-thought reasoning
const ThinkingBlock: React.FC<{
  content: string;
  status?: 'streaming' | 'complete';
  label: string;
  expanded: boolean;
  onToggle: () => void;
}> = ({ content, status, label, expanded, onToggle }) => {
  const { isDark } = useTheme();
  const isStreaming = status === 'streaming';

  return (
    <div
      className={`rounded-xl border overflow-hidden ${isDark ? 'border-cyan-500/15' : 'border-cyan-200'}`}
      style={{ background: 'var(--thinking-bg)' }}
    >
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-1.5 px-3 py-1.5 cursor-pointer transition-colors ${isDark ? 'hover:bg-cyan-500/[0.04]' : 'hover:bg-cyan-50'}`}
      >
        <svg
          className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''} ${isDark ? 'text-cyan-400/50' : 'text-cyan-500'}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
        <svg className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-cyan-400/50' : 'text-cyan-500'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
        </svg>
        <span className={`text-[10px] font-semibold uppercase tracking-widest ${isDark ? 'text-cyan-400/50' : 'text-cyan-500'}`}>
          {label}
        </span>
        {isStreaming && (
          <span className="ml-auto flex gap-0.5">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className={`w-1 h-1 rounded-full animate-bounce ${isDark ? 'bg-cyan-400/40' : 'bg-cyan-400'}`}
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </span>
        )}
      </button>
      {expanded && (
        <div className={`px-3 py-2.5 border-t ${isDark ? 'border-cyan-500/10' : 'border-cyan-100'}`}>
          <p className={`text-[12px] leading-relaxed whitespace-pre-wrap ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
            {content}
            {isStreaming && (
              <span className={`inline-block w-[2px] h-[13px] ml-[1px] align-middle animate-pulse ${isDark ? 'bg-cyan-400/50' : 'bg-cyan-500'}`} />
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Prompt block (violet / cyan variants) ───────────────────────────
const PromptBlock: React.FC<{
  content: string;
  status?: 'streaming' | 'complete';
  label: string;
  expanded: boolean;
  onToggle: () => void;
  variant: 'violet' | 'cyan';
}> = ({ content, status, label, expanded, onToggle, variant }) => {
  const { isDark } = useTheme();
  const isStreaming = status === 'streaming';
  const isViolet = variant === 'violet';

  const borderColor  = isViolet
    ? isDark ? 'border-violet-500/15' : 'border-violet-200'
    : isDark ? 'border-cyan-500/15' : 'border-cyan-200';
  const bgVar        = isViolet ? 'var(--prompt-bg-violet)' : 'var(--prompt-bg-cyan)';
  const hoverBg      = isViolet
    ? isDark ? 'hover:bg-violet-500/[0.04]' : 'hover:bg-violet-50'
    : isDark ? 'hover:bg-cyan-500/[0.04]' : 'hover:bg-cyan-50';
  const iconColor    = isViolet
    ? isDark ? 'text-violet-400/50' : 'text-violet-500'
    : isDark ? 'text-cyan-400/50' : 'text-cyan-500';
  const dotColor     = isViolet
    ? isDark ? 'bg-violet-400/40' : 'bg-violet-400'
    : isDark ? 'bg-cyan-400/40' : 'bg-cyan-400';
  const borderT      = isViolet
    ? isDark ? 'border-violet-500/10' : 'border-violet-100'
    : isDark ? 'border-cyan-500/10' : 'border-cyan-100';
  const cursorColor  = isViolet
    ? isDark ? 'bg-violet-400/50' : 'bg-violet-500'
    : isDark ? 'bg-cyan-400/50' : 'bg-cyan-500';
  const textColor    = isDark ? 'text-white/55' : 'text-slate-500';

  return (
    <div className={`rounded-xl border ${borderColor} overflow-hidden`} style={{ background: bgVar }}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-1.5 px-3 py-1.5 cursor-pointer ${hoverBg} transition-colors`}
      >
        <svg
          className={`w-3 h-3 ${iconColor} flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
        <svg className={`w-3 h-3 ${iconColor} flex-shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"/>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
        <span className={`text-[10px] font-semibold ${iconColor} uppercase tracking-widest`}>
          {label}
        </span>
        {isStreaming && (
          <span className="ml-auto flex gap-0.5">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className={`w-1 h-1 rounded-full ${dotColor} animate-bounce`}
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </span>
        )}
      </button>
      {expanded && (
        <div className={`px-3 py-2.5 border-t ${borderT}`}>
          <p className={`text-[12px] leading-relaxed ${textColor} whitespace-pre-wrap`}>
            {content}
            {isStreaming && (
              <span className={`inline-block w-[2px] h-[13px] ${cursorColor} ml-[1px] align-middle animate-pulse`} />
            )}
          </p>
        </div>
      )}
    </div>
  );
};

const TurnRow: React.FC<TurnRowProps> = ({ turn, sourceLanguage, processingLanguage, columnFocus }) => {
  const { isDark } = useTheme();
  const t = getT(sourceLanguage);
  const tRight = getT(processingLanguage);

  // Shared expanded state for left & right thinking blocks
  const hasThinking = !!(turn.rightAiThinking || turn.leftAiThinking);
  const isThinkingStreaming = turn.rightAiThinkingStatus === 'streaming' || turn.leftAiThinkingStatus === 'streaming';
  const [thinkingExpanded, setThinkingExpanded] = useState(hasThinking && isThinkingStreaming);
  React.useEffect(() => {
    if (isThinkingStreaming) setThinkingExpanded(true);
  }, [isThinkingStreaming]);
  const toggleThinking = React.useCallback(() => setThinkingExpanded(prev => !prev), []);

  // Shared expanded state for left & right refined prompt blocks
  const hasRefinedPrompt = !!(turn.refinedPrompt || turn.leftRefinedPrompt);
  const isPromptStreaming = turn.refinedPromptStatus === 'streaming' || turn.leftRefinedPromptStatus === 'streaming';
  const [promptExpanded, setPromptExpanded] = useState(hasRefinedPrompt && isPromptStreaming);
  React.useEffect(() => {
    if (isPromptStreaming) setPromptExpanded(true);
  }, [isPromptStreaming]);
  const togglePrompt = React.useCallback(() => setPromptExpanded(prev => !prev), []);

  const leftHidden  = columnFocus === 'right';
  const rightHidden = columnFocus === 'left';
  const showDivider = columnFocus === 'both';

  // Stable statusHint — only recompute when relevant statuses change
  const statusHint = useMemo(() => {
    if (turn.leftAiStatus === 'pending') {
      if (turn.rightAiStatus === 'streaming') return t.thinking;
      if (turn.rightUserStatus === 'translating' || turn.rightUserStatus === 'streaming') return t.translating;
    }
    if (turn.leftAiStatus === 'streaming') return t.translatingOutput;
    return undefined;
  }, [turn.leftAiStatus, turn.rightAiStatus, turn.rightUserStatus, t]);

  // ── Cell class helpers ──────────────────────────────────────────
  const lCell = leftHidden
    ? 'overflow-hidden'
    : `min-w-0 py-2 px-3 md:px-6 transition-colors duration-200${
        isDark ? ' group-hover:bg-violet-500/[0.018]' : ' group-hover:bg-violet-50/50'
      }${showDivider ? isDark ? ' border-r border-white/[0.04]' : ' border-r border-slate-200/60' : ''}`;
  const rCell = rightHidden
    ? 'overflow-hidden'
    : `min-w-0 py-2 px-3 md:px-6 transition-colors duration-200${
        isDark ? ' group-hover:bg-cyan-500/[0.018]' : ' group-hover:bg-cyan-50/50'
      }`;

  const hasRouting = !!turn.routingLabel;
  const hasError   = turn.status === 'error' && !!turn.error;

  return (
    <div
      className="relative grid group turn-row pt-2 md:pt-4 pb-2 md:pb-4"
      style={GRID_STYLES[columnFocus]}
    >
      {/* ── Row: Routing labels ────────────────────────────────────── */}
      {hasRouting && (
        <>
          <div className={lCell}>
            {!leftHidden && (
              <div className="flex items-center gap-1.5 px-1">
                <svg className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-violet-500/50' : 'text-violet-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span className={`text-[11px] tracking-wide ${isDark ? 'text-violet-400/55' : 'text-violet-500'}`}>
                  {t.routingBadgePrefix} {turn.routingLabel}
                </span>
              </div>
            )}
          </div>
          <div className={rCell}>
            {!rightHidden && (
              <div className="flex items-center gap-1.5 px-1">
                <svg className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-cyan-500/50' : 'text-cyan-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span className={`text-[11px] tracking-wide ${isDark ? 'text-cyan-400/55' : 'text-cyan-500'}`}>
                  {tRight.routingBadgePrefix} {turn.routingLabel}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Row: Refined / system prompts ──────────────────────────── */}
      {hasRefinedPrompt && (
        <>
          <div className={lCell}>
            {!leftHidden && turn.leftRefinedPrompt && (
              <PromptBlock
                content={turn.leftRefinedPrompt}
                status={turn.leftRefinedPromptStatus}
                label={t.refinedPromptLabel}
                expanded={promptExpanded}
                onToggle={togglePrompt}
                variant="violet"
              />
            )}
          </div>
          <div className={rCell}>
            {!rightHidden && turn.refinedPrompt && (
              <PromptBlock
                content={turn.refinedPrompt}
                status={turn.refinedPromptStatus}
                label={tRight.refinedPromptLabel}
                expanded={promptExpanded}
                onToggle={togglePrompt}
                variant="cyan"
              />
            )}
          </div>
        </>
      )}

      {/* ── Row: User messages ─────────────────────────────────────── */}
      <div className={lCell}>
        {!leftHidden && (
          <MessageBubble
            role="user"
            content={turn.leftUser}
            status="complete"
            sourceLanguage={sourceLanguage}
            variant="default"
          />
        )}
      </div>
      <div className={rCell}>
        {!rightHidden && (
          <MessageBubble
            role="user"
            content={turn.rightUser}
            status={turn.rightUserStatus}
            sourceLanguage={sourceLanguage}
            variant="processing"
          />
        )}
      </div>

      {/* ── Row: Thinking blocks ───────────────────────────────────── */}
      {hasThinking && (
        <>
          <div className={lCell}>
            {!leftHidden && turn.leftAiThinking && (
              <ThinkingBlock
                content={turn.leftAiThinking}
                status={turn.leftAiThinkingStatus}
                label={t.thinking}
                expanded={thinkingExpanded}
                onToggle={toggleThinking}
              />
            )}
          </div>
          <div className={rCell}>
            {!rightHidden && turn.rightAiThinking && (
              <ThinkingBlock
                content={turn.rightAiThinking}
                status={turn.rightAiThinkingStatus}
                label={tRight.thinking}
                expanded={thinkingExpanded}
                onToggle={toggleThinking}
              />
            )}
          </div>
        </>
      )}

      {/* ── Row: AI responses ──────────────────────────────────────── */}
      <div className={lCell}>
        {!leftHidden && (
          <MessageBubble
            role="ai"
            content={turn.leftAi}
            status={turn.leftAiStatus}
            sourceLanguage={sourceLanguage}
            variant="default"
            statusHint={statusHint}
          />
        )}
      </div>
      <div className={rCell}>
        {!rightHidden && (
          <MessageBubble
            role="ai"
            content={turn.rightAi}
            status={turn.rightAiStatus}
            sourceLanguage={sourceLanguage}
            variant="processing"
          />
        )}
      </div>

      {/* ── Row: Error ─────────────────────────────────────────────── */}
      {hasError && (
        <>
          <div className={lCell}>
            {!leftHidden && (
              <p className="text-[12px] text-red-400/75 pl-[28px]">{turn.error}</p>
            )}
          </div>
          <div className={rCell} />
        </>
      )}

      {/* Dual-tone bottom separator */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'var(--sep-row)' }}
      />
    </div>
  );
};

export default React.memo(TurnRow);
